/**
 * Wiring for a `uib-tabset` whose tabs are states of their own.
 *
 * The tab bar is the only thing that decides which tab is showing, and it defaults to the first
 * one. Its `select` then navigates - so arriving anywhere but the first tab, by URL or because the
 * AI omnibar was asked for that tab by name, selects tab one on init and sends you straight back
 * to it. The tab bar has to follow the state instead of setting it.
 *
 * The page passes its own tabs, in the order the template renders them.
 *
 *     const tabs = bindStateTabs($scope, $state, [
 *         "app.patients.careAndTaskManagement.upcomingTasks",
 *         "app.patients.careAndTaskManagement.careManagement",
 *     ]);
 *     $scope.goToState = tabs.goToState;
 *
 * The template binds `active="activeTab"` on the tabset and gives every tab its `index`, so a tab
 * removed by an `ng-if` cannot shift the rest.
 */
export function bindStateTabs($scope, $state, tabStates) {
    // Falls back to the first tab, whose own `select` is what sends someone who opened the page
    // itself - the parent state, which shows no tab - on to the tab the page opens with.
    $scope.activeTab = Math.max(0, indexOfState($state.current.name));

    // The controller belongs to the parent state, so it is constructed while the transition to the
    // tab is still running and `$state.current` is still the page we came from. The tab to show is
    // known only once the transition lands.
    $scope.$watch(
        () => $state.current.name,
        (name) => {
            const index = indexOfState(name);

            if (index !== -1) {
                $scope.activeTab = index;
            }
        }
    );

    return {
        goToState: (state) => {
            const pending = pendingState();

            // The tab bar catching up with a tab we are already on our way to, which is the init
            // case above: tab one is selected before the transition lands, and obeying it here is
            // exactly the redirect this is here to prevent.
            if (pending !== null && pending !== state && indexOfState(pending) !== -1) {
                return;
            }

            if ($state.current.name !== state) {
                $state.go(state);
            }
        }
    };

    function indexOfState(name) {
        return tabStates.findIndex((state) => name === state || String(name).startsWith(state + "."));
    }

    function pendingState() {
        const transition = $state.transition;

        return transition && typeof transition.to === "function" ? transition.to().name : null;
    }
}
